import * as yup from 'yup';
import { ApplicableStatutoryAuthorities, ErrorMessages } from '@src/utils/enums';
import { decimalNumberRegex, integerRegex, latRegex, lngRegex } from '@src/utils/regex';
import { populateDredgeTypes, RequestedPermitTypes } from './PermitsForm.helper';

export const otherNwpValidationSchema = yup.object().shape({
  approvalType: yup.string().required(ErrorMessages.SelectOption),
  explanation: yup
    .string()
    .when('approvalType', { is: 'Other', then: () => yup.string().required(ErrorMessages.Required) })
    .nullable(),
  fileNumber: yup.string().required(ErrorMessages.Required),
  appliedDate: yup.string().required(ErrorMessages.Required),
  approvedDate: yup.string().nullable(),
  deniedDate: yup.string().nullable(),
});

export const relatedAppsValidationSchema = yup.object().shape({
  agency: yup.string().required(ErrorMessages.Required),
  approvalType: yup.string().required(ErrorMessages.Required),
  certIDNumber: yup.string().required(ErrorMessages.Required),
  appliedDate: yup.string().required(ErrorMessages.Required),
  approvalDate: yup.string().nullable(),
  deniedDate: yup.string().nullable(),
});

const dredgingSchema = {
  isDredgingNavWaters: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section10),
      then: () => yup.string().required(ErrorMessages.SelectOption),
    })
    .nullable(),
  materialTypeComposition: yup
    .string()
    .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required(ErrorMessages.Required) })
    .nullable(),
  dredgeMethod: yup
    .string()
    .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required(ErrorMessages.Required) })
    .nullable(),
  constructionMethods: yup
    .string()
    .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required(ErrorMessages.Required) })
    .nullable(),
  estimatedNumberOfEvents: yup
    .string()
    .when(['estimatedNumberOfEvents', 'isDredgingNavWaters'], (estimatedNumberOfEvents, isDredgingNavWaters) =>
      estimatedNumberOfEvents && isDredgingNavWaters === 'true'
        ? yup.string().matches(integerRegex, { message: ErrorMessages.Invalid, excludeEmptyString: true })
        : yup.string().nullable()
    ),
  workStartDate: yup
    .string()
    .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required(ErrorMessages.Required) })
    .nullable(),
  workEndDate: yup
    .string()
    .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required(ErrorMessages.Required) })
    .nullable()
    .test({
      test: (value, { parent: { isDredgingNavWaters } }) => (isDredgingNavWaters === 'true' ? value !== '' : true),
      message: ErrorMessages.Required,
    })
    .test({
      test: (value, { parent: { workStartDate } }) => !value || new Date(value) >= new Date(workStartDate),
      message: 'The end date must be after the start date',
    }),
  durationOfWork: yup
    .string()
    .when('isDredgingNavWaters', {
      is: 'true',
      then: () =>
        yup
          .number()
          .required(ErrorMessages.Required)
          .typeError(ErrorMessages.Invalid)
          .positive(ErrorMessages.GreatThanZero),
    })
    .nullable(),
  unitOfWork: yup
    .string()
    .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required(ErrorMessages.SelectOption) })
    .nullable(),
  currentDepth: yup
    .string()
    .when('isDredgingNavWaters', {
      is: 'true',
      then: () => yup.string().required(ErrorMessages.Required).matches(decimalNumberRegex, ErrorMessages.Invalid),
    })
    .nullable(),
  dredgeType: yup
    .array()
    .when('isDredgingNavWaters', {
      is: 'true',
      then: () => yup.array().min(1, 'Please select at least one value'),
    })
    .nullable(),
  lastDredgeCycle: yup
    .string()
    .when('dredgeType', {
      is: (val) => val?.includes('Maintenance'),
      then: () => yup.string().required(ErrorMessages.Required),
    })
    .nullable(),
  authorizedLength: yup
    .string()
    .when('dredgeType', {
      is: (val) => val?.length > 0,
      then: () => yup.string().required(ErrorMessages.Required).matches(decimalNumberRegex, ErrorMessages.Invalid),
    })
    .nullable(),
  authorizedWidth: yup
    .string()
    .when('dredgeType', {
      is: (val) => val?.length > 0,
      then: () => yup.string().required(ErrorMessages.Required).matches(decimalNumberRegex, ErrorMessages.Invalid),
    })
    .nullable(),
  authorizedDepth: yup
    .string()
    .when('dredgeType', {
      is: (val) => val?.length > 0,
      then: () => yup.string().required(ErrorMessages.Required).matches(decimalNumberRegex, ErrorMessages.Invalid),
    })
    .nullable(),
  disposalPlan: yup
    .string()
    .when('dredgeType', {
      is: (val) => val?.length > 0,
      then: () => yup.string().required(ErrorMessages.Required),
    })
    .nullable(),
  estNumDisposalTrips: yup
    .string()
    .when(['estNumDisposalTrips', 'dredgeType'], (estNumDisposalTrips, dredgeType) =>
      estNumDisposalTrips && dredgeType?.length > 0
        ? yup.string().matches(integerRegex, { message: ErrorMessages.Invalid, excludeEmptyString: true })
        : yup.string().nullable()
    ),
  dredgeDisposalLatitude: yup
    .mixed()
    .when('dredgeType', {
      is: (val) => val?.length > 0,
      then: () => yup.string().required(ErrorMessages.Required).matches(latRegex, ErrorMessages.InvalidLatitudeFormat),
    })
    .nullable(),
  dredgeDisposalLongitude: yup
    .mixed()
    .when('dredgeType', {
      is: (val) => val?.length > 0,
      then: () => yup.string().required(ErrorMessages.Required).matches(lngRegex, ErrorMessages.InvalidLongitudeFormat),
    })
    .nullable(),
};

const dischargesSchema = {
  dischargeMaterialSource: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section404),
      then: () => yup.string().required(ErrorMessages.Required),
    })
    .nullable(),
  dischargeReason: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section404),
      then: () => yup.string().required(ErrorMessages.Required),
    })
    .nullable(),
};

const dredgeTransportationSchema = {
  isSiteDesignatedForDISP: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
      then: () => yup.string().required(ErrorMessages.SelectOption),
    })
    .nullable(),
  disposalSiteCharacteristics: yup
    .string()
    .when('isSiteDesignatedForDISP', { is: 'false', then: () => yup.string().required(ErrorMessages.Required) })
    .nullable(),
  knownDredgeMaterialDischarges: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
      then: () => yup.string().required(ErrorMessages.Required),
    })
    .nullable(),
  previousDisposals: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
      then: () => yup.string().required(ErrorMessages.Required),
    })
    .nullable(),
  estNumberOfMonthsOnSite: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
      then: () => yup.string().matches(integerRegex, 'Must be an integer').required(ErrorMessages.Required),
    })
    .nullable(),
  materialSource: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
      then: () => yup.string().required(ErrorMessages.Required),
    })
    .nullable(),
  materialTypeAndComp: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
      then: () => yup.string().required(ErrorMessages.Required),
    })
    .nullable(),
  transportationAndDisposalMethod: yup
    .string()
    .when('applicableStatutoryAuthority', {
      is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
      then: () => yup.string().required(ErrorMessages.Required),
    })
    .nullable(),
};

const commonPermitSchema = {
  requestedPermitType: yup.string().required(ErrorMessages.SelectOption),
  applicableStatutoryAuthority: yup
    .array()
    .when('requestedPermitType', {
      is: (val) => val !== null && val !== '',
      then: (schema) => schema.min(1, 'Please select at least one value'),
    })
    .nullable(),
  navigableWatersAdditionalInfo: yup.string().nullable(),
  isAnyWorkComplete: yup
    .string()
    .when('requestedPermitType', {
      is: (val) => val !== null && val !== '',
      then: (schema) => schema.required(ErrorMessages.SelectOption),
    })
    .nullable(),
  completedWorkDescription: yup
    .string()
    .when('isAnyWorkComplete', { is: 'true', then: () => yup.string().required(ErrorMessages.Required) })
    .nullable(),
  isCoastalDistrict: yup.boolean(),
  isInTerrSeasOrOceanWaters: yup.string().when(['requestedPermitType', 'isCoastalDistrict'], {
    is: (requestedPermitType, isCoastalDistrict) => !!requestedPermitType && isCoastalDistrict === true,
    then: (schema) => schema.required(ErrorMessages.SelectOption),
    otherwise: (schema) => schema.nullable(),
  }),
  activityBaselineRelationship: yup
    .string()
    .when('isInTerrSeasOrOceanWaters', { is: 'true', then: () => yup.string().required(ErrorMessages.Required) })
    .nullable(),
  ...dredgingSchema,
};

const schemaDependencies = [
  ['estimatedNumberOfEvents', 'estimatedNumberOfEvents'],
  ['estNumDisposalTrips', 'estNumDisposalTrips'],
  ['existingVesselSlips', 'existingVesselSlips'],
];

const gpSchema = yup.object().shape(
  {
    ...commonPermitSchema,
    gpPermitTypes: yup.array().min(1, ErrorMessages.SelectOption),
    otherNwp: yup
      .boolean()
      .test('check-table-length-and-errors', 'Other Nationwide Permits: Table contains invalid rows', function () {
        const { otherNwpTableLength, otherNwpRowErrors } = this.options.context || {};
        const isEmpty = otherNwpTableLength < 1;
        return isEmpty ? true : !otherNwpRowErrors;
      }),
  },
  schemaDependencies
);

const ipSchema = yup.object().shape(
  {
    ...commonPermitSchema,
    developmentType: yup.string().required(ErrorMessages.SelectOption),
    hasRelatedAgencyApplications: yup.string().required(ErrorMessages.SelectOption),
    relatedAppsTable: yup.boolean().when('hasRelatedAgencyApplications', {
      is: (val) => val === 'true',
      then: (schema) =>
        schema.test(
          'check-table-length',
          'Previous Permits, Certificates... : At least one certificate, approval, or denial is required',
          function () {
            const { relatedAppsTableLength } = this.options.context || {};
            const isEmpty = relatedAppsTableLength < 1;
            return !isEmpty;
          }
        ),
      otherwise: (schema) => schema.nullable(),
    }),
    projectWorkStartDate: yup.string().required(ErrorMessages.Required),
    projectWorkEndDate: yup.string().required(ErrorMessages.Required),
    ...dischargesSchema,
    ...dredgeTransportationSchema,
  },
  schemaDependencies
);

export const permitSchema = yup.lazy((values) => {
  if (!values) {
    return yup.object({});
  }
  switch (values?.requestedPermitType) {
    case RequestedPermitTypes.GeneralPermit:
      return gpSchema;
    case RequestedPermitTypes.StandardPermit:
    case RequestedPermitTypes.LetterOfPermission:
      return ipSchema;
    default:
      return yup.object().shape(commonPermitSchema, schemaDependencies);
  }
});

const getCommonDefaultValues = ({ permitData, district }) => ({
  requestedPermitType: permitData?.requestedPermitType ?? '',
  applicableStatutoryAuthority: permitData?.applicableStatutoryAuthorities ?? [],
  navigableWatersAdditionalInfo: permitData?.navigableWatersAdditionalInfo ?? '',
  isAnyWorkComplete:
    permitData?.isAnyWorkComplete !== undefined && permitData?.isAnyWorkComplete !== null
      ? permitData.isAnyWorkComplete
        ? 'true'
        : 'false'
      : '',
  completedWorkDescription: permitData?.completedWorkDescription ?? '',
  isCoastalDistrict: district?.isCoastalDistrict ?? false,
  isInTerrSeasOrOceanWaters:
    permitData?.isInTerrSeasOrOceanWaters !== undefined && permitData?.isInTerrSeasOrOceanWaters !== null
      ? permitData.isInTerrSeasOrOceanWaters
        ? 'true'
        : 'false'
      : '',
  // Dredge Project
  isDredgingNavWaters:
    permitData?.isDredgingNavWaters !== undefined && permitData?.isDredgingNavWaters !== null
      ? permitData.isDredgingNavWaters
        ? 'true'
        : 'false'
      : '',
  materialTypeComposition: permitData?.dredgeProject?.materialTypeComposition ?? '',
  dredgeMethod: permitData?.dredgeProject?.dredgeMethod ?? '',
  constructionMethods: permitData?.dredgeProject?.constructionMethods ?? '',
  estimatedNumberOfEvents: permitData?.dredgeProject?.estimatedNumberOfEvents ?? '',
  workStartDate: permitData?.dredgeProject?.workStartDate?.split('T')?.[0] ?? '',
  workEndDate: permitData?.dredgeProject?.workEndDate?.split('T')?.[0] ?? '',
  durationOfWork: permitData?.dredgeProject?.durationOfWork ?? null,
  unitOfWork: permitData?.dredgeProject?.unitOfWork ?? '',
  currentDepth: permitData?.dredgeProject?.currentDepth ?? '',
  dredgeType: populateDredgeTypes(permitData),
  lastDredgeCycle: permitData?.dredgeProject?.lastDredgeCycle?.split('T')?.[0] ?? '',
  authorizedLength: permitData?.dredgeProject?.authorizedLength ?? '',
  authorizedWidth: permitData?.dredgeProject?.authorizedWidth ?? '',
  authorizedDepth: permitData?.dredgeProject?.authorizedDepth ?? '',
  disposalPlan: permitData?.dredgeProject?.disposalPlan ?? '',
  estNumDisposalTrips: permitData?.dredgeProject?.estNumDisposalTrips ?? '',
  dredgeDisposalLatitude:
    typeof permitData?.dredgeProject?.dredgeDisposalLatitude === 'number'
      ? permitData?.dredgeProject?.dredgeDisposalLatitude?.toFixed(7)
      : (permitData?.dredgeProject?.dredgeDisposalLatitude ?? ''),
  dredgeDisposalLongitude:
    typeof permitData?.dredgeProject?.dredgeDisposalLongitude === 'number'
      ? permitData?.dredgeProject?.dredgeDisposalLongitude?.toFixed(7)
      : (permitData?.dredgeProject?.dredgeDisposalLongitude ?? ''),
});

const getGpDefaultValues = ({ permitData }) => ({
  gpPermitTypes:
    permitData?.gpPermitTypes?.map((item) => ({
      value: String(item.permitTypeID),
      label: item.permitType ? `${item.value} (${item.permitType})` : item.value,
    })) ?? [],
  supportingPermits: permitData?.supportingPermits
    ? permitData?.supportingPermits?.map((item) => ({
        ...item,
        appliedDate: item?.appliedDate?.split('T')?.[0],
        approvedDate: item?.approvedDate?.split('T')?.[0],
        deniedDate: item?.deniedDate?.split('T')?.[0],
      }))
    : [],
  activityDescription: permitData?.activityDescription ?? '',
  activityPurposeAndNeed: permitData?.activityPurposeAndNeed ?? '',
});

const getIpDefaultValues = ({ permitData }) => ({
  developmentType: permitData?.developmentType ?? '',
  hasRelatedAgencyApplications:
    permitData?.hasRelatedAgencyApplications !== undefined && permitData?.hasRelatedAgencyApplications !== null
      ? permitData.hasRelatedAgencyApplications
        ? 'true'
        : 'false'
      : '',
  projectWorkStartDate: permitData?.workStartDate?.split('T')?.[0] ?? '',
  projectWorkEndDate: permitData?.workEndDate?.split('T')?.[0] ?? '',
  relatedAgencyApplications: permitData?.relatedAgencyApplications
    ? permitData?.relatedAgencyApplications?.map((item) => ({
        ...item,
        appliedDate: item?.appliedDate?.split('T')?.[0],
        approvedDate: item?.approvedDate?.split('T')?.[0],
        deniedDate: item?.deniedDate?.split('T')?.[0],
      }))
    : [],
  activityBaselineRelationship: permitData?.activityBaselineRelationship ?? '',
  // Dredge Transportation
  isSiteDesignatedForDISP: permitData?.dredgedTransportation?.isSiteDesignatedForDISP?.toString() || '',
  knownDredgeMaterialDischarges: permitData?.dredgedTransportation?.knownDredgeMaterialDischarges ?? '',
  disposalSiteCharacteristics: permitData?.dredgedTransportation?.disposalSiteCharacteristics ?? '',
  previousDisposals: permitData?.dredgedTransportation?.previousDisposals ?? '',
  estNumberOfMonthsOnSite: permitData?.dredgedTransportation?.estNumberOfMonthsOnSite ?? '',
  materialSource: permitData?.dredgedTransportation?.materialSource ?? '',
  materialTypeAndComp: permitData?.dredgedTransportation?.materialTypeAndComp ?? '',
  transportationAndDisposalMethod: permitData?.dredgedTransportation?.transportationAndDisposalMethod ?? '',
  // Discharges
  dischargeMaterialSource: permitData?.discharges_404?.materialSource ?? '',
  dischargeReason: permitData?.discharges_404?.dischargeReason ?? '',
});

export const getPermitDefaultValues = ({ permitData, district }) => ({
  ...getCommonDefaultValues({ permitData, district }),
  ...getGpDefaultValues({ permitData }),
  ...getIpDefaultValues({ permitData }),
});
